import { useEffect, useState } from "react";

import useAuth from "hooks/useAuth";
import axios from "utils/axios";

// Shared poller for new contact-form inquiries. The bell, the sidebar chip
// and the dashboard card all read the same store, so there is one request
// per interval no matter how many components are mounted.

const POLL_MS = 60 * 1000;
const PLATFORM_ROLES = ["admin", "superadmin"];
export const CONTACT_INQUIRIES_CHANGED = "contact-inquiries:changed";

const EMPTY = {
  counts: { new: 0, contacted: 0, closed: 0, all: 0 },
  latestNew: [],
};

let state = { data: EMPTY, loading: true, error: null };
const listeners = new Set();
let timer = null;
let inflight = null;

const emit = () => listeners.forEach((listener) => listener(state));

const fetchSummary = () => {
  if (inflight) return inflight;
  inflight = axios
    .get("/contact-inquiries/summary")
    .then((res) => {
      state = { data: res.data?.data || EMPTY, loading: false, error: null };
    })
    .catch((err) => {
      state = { ...state, loading: false, error: err?.message || "error" };
    })
    .finally(() => {
      inflight = null;
      emit();
    });
  return inflight;
};

const onVisible = () => {
  if (document.visibilityState === "visible") fetchSummary();
};

const start = () => {
  if (timer) return;
  fetchSummary();
  timer = setInterval(() => {
    // Skip polling while the tab is hidden; refresh when it comes back.
    if (document.visibilityState === "visible") fetchSummary();
  }, POLL_MS);
  window.addEventListener(CONTACT_INQUIRIES_CHANGED, fetchSummary);
  document.addEventListener("visibilitychange", onVisible);
};

const stop = () => {
  clearInterval(timer);
  timer = null;
  window.removeEventListener(CONTACT_INQUIRIES_CHANGED, fetchSummary);
  document.removeEventListener("visibilitychange", onVisible);
};

// Call after changing an inquiry so every badge refreshes right away.
export const notifyContactInquiriesChanged = () =>
  window.dispatchEvent(new Event(CONTACT_INQUIRIES_CHANGED));

const useContactInquirySummary = () => {
  const { user } = useAuth();
  const enabled = PLATFORM_ROLES.includes(user?.role);
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setSnapshot);
    setSnapshot(state);
    start();
    return () => {
      listeners.delete(setSnapshot);
      if (!listeners.size) stop();
    };
  }, [enabled]);

  return {
    enabled,
    counts: snapshot.data.counts || EMPTY.counts,
    latestNew: snapshot.data.latestNew || [],
    loading: enabled && snapshot.loading,
    error: snapshot.error,
    refresh: fetchSummary,
  };
};

export default useContactInquirySummary;
